import { useNavigate, useParams } from "react-router-dom"
import { useGetModelFamiliesModelFamilyGet, useGetModelsModelGet } from "../../../api/generated/fastAPI";
import { useEffect, useState, MouseEvent, ReactNode, useMemo, Dispatch, SetStateAction } from "react";
import { Model, ModelFamily } from "../../../api/models";
import { convertNumberToDecimal, exportData, getStatsCellColor } from "../../../utils/formattingFns";
import { TableData, TableDataField } from "../../../components/Table/DataTable";
import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import Dropdown, { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import { modelStatLimits, modelStats } from "../../../utils/supplementalData";
import { capitalize } from "../../../utils/formattingFns";
import TruthTable from "../../../components/Data/TruthTable";
import PlotlyGraphs from "../../../components/Data/PlotlyGraphs";
import Plot from "react-plotly.js";
import HorizontalNumberSlider from "../../../components/Data/HorizontalNumberSlider";
// import useMessage from "../../../hooks/useMessage";
import useMessage from "../../../hooks/useMessage";
import SnackBar from "../../../components/Alerts/SnackBar";

import DeleteModal from "../../../components/Popups/DeleteModal";
import { useAccess } from "../../../access/useAccess";
import { demo_access } from "../../../access/predefinedConditions";
import { Skeleton } from "@mui/material";
import useDownload from "../../../hooks/endpoints/useDownload";
import { formatDisplayedModelMetadata, formatModelData, getModelTaskType, MetadataMenuItemType } from "../../../utils/dataFormattingFns";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";
import useUpdate from "../../../hooks/endpoints/useUpdate";
import { ReactComponent as FingerprintIcon } from '../../../assets/Icons/adornments/Fingerprint.svg';
const InfoContainer = ({ label, children }: { label: string, children: ReactNode }) => {
    return (
        <div className="w-full flex flex-col gap-2 justify-start items-start">
            <label className="font-medium text-[12px] text-default text-nowrap">{label}</label>
            {children}
        </div>
    )
};

const ModelStat = ({ name, value }: { name: string, value: string }) => {
    let color = '';
    const modelLimit = modelStatLimits.find(stat => stat.value === name);
    if (modelLimit) {
        color = getStatsCellColor({ min: modelLimit.min, max: modelLimit.max }, Number(value), false);
    } else {
        color = '#E0E0E0';
    }

    return (
        <div style={{backgroundColor: color}} className="flex justify-center items-center py-1 px-3 rounded-lg text-default font-semibold">
            {convertNumberToDecimal(value)}
        </div>
    )
};

const plotOrder = [
    {
        type: 'overlay',
        index: 0,
    },
    {
        type: 'cross_val_predict',
        index: 0,
    },
    {
        type: 'truthTable',
        index: 1,
    },
    {
        type: 'roc',
        index: 2,
    }
]

const toFiniteNumber = (value: unknown): number | null => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

const toFiniteArray = (value: unknown): number[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((item) => Number(item))
        .filter((item) => Number.isFinite(item));
};

const getMetricNumber = (stats: any, key: string): number | null => {
    const rawValue = stats?.[key];
    if (Array.isArray(rawValue)) {
        return toFiniteNumber(rawValue[0]);
    }
    return toFiniteNumber(rawValue);
};

const DCM_SIGMA_MIN = 0.005;
const DCM_SIGMA_MAX = 0.15;

const clampDcmSigma = (sigma: number): number => {
    if (!Number.isFinite(sigma)) {
        return 0.05;
    }
    return Math.min(Math.max(sigma, DCM_SIGMA_MIN), DCM_SIGMA_MAX);
};

const buildNormalizedDensityHeatmap = (x: number[], y: number[], bins = 50) => {
    if (x.length === 0 || y.length === 0) {
        return null;
    }

    const xMin = Math.min(...x);
    const xMax = Math.max(...x);
    const yMin = Math.min(...y);
    const yMax = Math.max(...y);

    if (!Number.isFinite(xMin) || !Number.isFinite(xMax) || !Number.isFinite(yMin) || !Number.isFinite(yMax) || xMin === xMax || yMin === yMax) {
        return null;
    }

    const xStep = (xMax - xMin) / bins;
    const yStep = (yMax - yMin) / bins;
    const counts = Array.from({ length: bins }, () => Array.from({ length: bins }, () => 0));

    for (let i = 0; i < x.length; i += 1) {
        const xiRaw = Math.floor((x[i] - xMin) / xStep);
        const yiRaw = Math.floor((y[i] - yMin) / yStep);
        const xi = Math.min(Math.max(xiRaw, 0), bins - 1);
        const yi = Math.min(Math.max(yiRaw, 0), bins - 1);
        counts[xi][yi] += 1;
    }

    const normalized = counts.map((column) => {
        const colSum = column.reduce((sum, item) => sum + item, 0);
        if (colSum === 0) {
            return column.map(() => 0);
        }
        return column.map((item) => item / colSum);
    });

    const xCenters = Array.from({ length: bins }, (_, idx) => xMin + xStep * (idx + 0.5));
    const yCenters = Array.from({ length: bins }, (_, idx) => yMin + yStep * (idx + 0.5));
    const z = Array.from({ length: bins }, (_, yIdx) => Array.from({ length: bins }, (_, xIdx) => normalized[xIdx][yIdx]));

    return {
        xCenters,
        yCenters,
        z,
    };
};

const densityColorScale: Array<[number, string]> = [
    [0, '#ffffff'],
    [0.2, '#dbeafe'],
    [0.45, '#93c5fd'],
    [0.7, '#3b82f6'],
    [1, '#1e3a8a'],
];

export default function IndividualModel() {
    const { model_family_id, model_id } = useParams();
    const [ model, setModel ] = useState<Model | null>(null); 
    const [ modelFamily, setModelFamily ] = useState<ModelFamily | null>(null);
    const [ modelData, setModelData ] = useState<TableData | null>(null);
    const [ modelMenuMetadata, setModelMenuMetadata ] = useState<MetadataMenuItemType[]>([]);
    const [ plotTypes, setPlotTypes ] = useState<string[]>([]);

    const [ stats, setStats ] = useState<TableDataField[]>([]);
    const [ modelTaskType, setModelTaskType ] = useState<'classification' | 'regression' | 'dcm'>('classification');
    const [ dcmDensityMode, setDcmDensityMode ] = useState<'hist2d' | 'hist2d_normalized'>('hist2d');
    const [ dcmSigma, setDcmSigma ] = useState<number>(0.05);
    const [ useAutoDcmSigma, setUseAutoDcmSigma ] = useState<boolean>(true);

    const { canAccess } = useAccess();
    const { downloadModelStats } = useDownload();

    const {
        open,
        setOpen,
        message,
        isSuccess,
        setPopupMessage
    } = useMessage();


    useEffect(() => {
        if (modelData) {
            setStats(modelData.fields.filter(field => modelStats.includes(field.associatedColumn)));
        }
    }, [modelData]);

    useEffect(() => {
        if (modelTaskType !== 'dcm') {
            setUseAutoDcmSigma(true);
            setDcmSigma(0.05);
        }
    }, [modelTaskType, model_id]);

    const navigate = useNavigate();

    const getModelMutation = useGetModelsModelGet({
        model_hash: model_id
    });

    const getModelFamilyMutation = useGetModelFamiliesModelFamilyGet({ 
        model_family_id,
        include_airflow_metadata: false 
    });

    const { updateModel } = useUpdate({
        itemToUpdate: model,
        setPopupMessage,
        refetchFn: () => getModelMutation.refetch(),
    });

     useEffect(() => {
        if (getModelFamilyMutation.data) {
            const data = getModelFamilyMutation.data as unknown as ModelFamily;
            setModelFamily(data);
        }
    }, [getModelFamilyMutation.data]);

    useEffect(() => {
        if (getModelMutation.data) {
            const data = getModelMutation.data as unknown as Model;
            setModel(data);
            setModelTaskType(getModelTaskType(data));

            const { modelData } = formatModelData([data]);

            const formattedData : TableData | null = modelData.length > 0 ? modelData[0] : null;

            let plots: string[] = [];

            if (formattedData && formattedData.fields.length > 0) {
                formattedData.fields.forEach(field => {
                    if (field.type === 'plot') {
                        plots.push(field.associatedColumn);
                    }
                })
            };

            if (plots.length > 0) {
                let plotArr = new Array(plots.length).fill(null);

                plots.forEach(plot => {
                    const matchingPlot = plotOrder.find(ord => ord.type === plot);
                    if (matchingPlot) {
                        plotArr[matchingPlot.index] = plot;
                    }
                });

                setPlotTypes(plotArr);
            };

            setModelData(formattedData);
        }
    }, [getModelMutation.data]);

    useEffect(() => {
        const fetchModelMenuMetadata = async (modelFamilyName: string, mdl: Model) => {
            const metadata = await formatDisplayedModelMetadata(modelFamilyName, mdl);
            setModelMenuMetadata(metadata);
        };

        if (modelFamily && model) {
            fetchModelMenuMetadata(modelFamily.name ?? '', model);
        }
    }, [modelFamily, model]);

    const getMethodName = () => {
        if (modelData) {
            const fields = modelData.fields;

            const methodField = fields.find(field => field.associatedColumn === 'method');

            if (methodField) {
                return methodField.value as string;
            } else {
                if (modelTaskType === 'dcm') {
                    return 'DomainClassificationModel';
                }
                return '';
            }
        } else {
            return ''
        }
    };

    const dcmMetadataRows = useMemo(() => {
        if (!model || modelTaskType !== 'dcm') return [];

        const modelMetadata: any = model.model_metadata ?? {};
        const dcmMetadata: any = modelMetadata.dcm_metadata ?? {};
        const dcmJobMetadata: any = dcmMetadata.job_metadata ?? {};
        const runtimeMetrics: any = dcmMetadata.runtime_metrics ?? {};

        const rows: { label: string, value: string }[] = [];

        const convergenceThreshold = dcmJobMetadata.convergence_threshold;
        if (convergenceThreshold !== undefined && convergenceThreshold !== null) {
            rows.push({ label: 'Convergence Threshold', value: String(convergenceThreshold) });
        }

        const sourceName = dcmJobMetadata.unified_source_name;
        if (sourceName) {
            rows.push({ label: 'Unified Source Name', value: String(sourceName) });
        }

        const sourceVersion = dcmJobMetadata.unified_source_version;
        if (sourceVersion) {
            rows.push({ label: 'Unified Source Version', value: String(sourceVersion) });
        }

        const structureRepresentation = dcmJobMetadata.unified_structure_representation;
        if (structureRepresentation) {
            rows.push({ label: 'Structure Representation', value: String(structureRepresentation) });
        }

        const fingerprintType = dcmJobMetadata.fingerprint_type;
        if (fingerprintType) {
            rows.push({ label: 'Fingerprint Type', value: String(fingerprintType) });
        }

        const sampleCount = runtimeMetrics.ood_sample_count;
        if (sampleCount !== undefined && sampleCount !== null) {
            rows.push({ label: 'OOD Sample Count', value: String(sampleCount) });
        }

        const samplingStrategy = runtimeMetrics.sampling_strategy;
        if (samplingStrategy) {
            rows.push({ label: 'Sampling Strategy', value: String(samplingStrategy) });
        }

        return rows;
    }, [model, modelTaskType]);

    const dcmPlotData = useMemo(() => {
        if (!model || modelTaskType !== 'dcm') {
            return {
                metricCards: [] as { label: string, value: number }[],
                quantilePlot: null as null | {
                    xOutPlot: number[],
                    xMin: number,
                    xMax: number,
                    sigma: number,
                    yOutProbMin: number[],
                    yOutProbLow: number[],
                    yOutProbExp: number[],
                    yOutProbMid: number[],
                    yOutProbHigh: number[],
                    yOutProbMax: number[],
                    yOutTrue: number[],
                    yOutProbBinary: number[],
                },
                density: null as null | {
                    x: number[],
                    y: number[],
                    xMin: number,
                    xMax: number,
                    normalized: ReturnType<typeof buildNormalizedDensityHeatmap>,
                },
                histogram: null as null | {
                    xDomain: number[],
                    xMin: number,
                    xMax: number,
                    xOutMin: number,
                    xOutMax: number,
                },
            };
        }

        const modelMetadata: any = model.model_metadata ?? {};
        const dcmMetadata: any = modelMetadata.dcm_metadata ?? {};
        const cvPerfDict: any = dcmMetadata.submodel_nested_cv_stats?.cv_performance_df_dict ?? {};
        const truePred: any = cvPerfDict.true_pred ?? {};
        const stats: any = cvPerfDict.stats ?? {};

        const metricSpecs = [
            { key: 'auc', label: 'AUC' },
            { key: 'balanced_accuracy', label: 'Balanced Accuracy' },
            { key: 'geometric_balanced_accuracy', label: 'Geometric Balanced Accuracy' },
            { key: 'mcc', label: 'MCC' },
            { key: 'f1score', label: 'F1 Score' },
            { key: 'precision', label: 'Precision' },
            { key: 'recall', label: 'Recall' },
            { key: 'specificity', label: 'Specificity' },
            { key: 'cohens_kappa', label: "Cohen's Kappa" },
        ];
        const metricCards = metricSpecs
            .map((spec) => ({
                label: spec.label,
                value: getMetricNumber(stats, spec.key),
            }))
            .filter((metric): metric is { label: string, value: number } => metric.value !== null);

        const requiredChannels = {
            xDomain: toFiniteArray(truePred.y_prob_out_of_domain),
            yProbMin: toFiniteArray(truePred.y_prob_1_min),
            yProbLow: toFiniteArray(truePred.y_prob_1_low),
            yProbExp: toFiniteArray(truePred.y_prob_1_exp),
            yProbMid: toFiniteArray(truePred.y_prob_1_mid),
            yProbHigh: toFiniteArray(truePred.y_prob_1_high),
            yProbMax: toFiniteArray(truePred.y_prob_1_max),
            yTrue: toFiniteArray(truePred.y_true),
            yProbBinary: toFiniteArray(truePred.y_prob_binary),
        };

        const lengths = Object.values(requiredChannels).map((arr) => arr.length);
        const minLen = lengths.length > 0 ? Math.min(...lengths) : 0;

        if (minLen < 5 || lengths.some((len) => len === 0)) {
            return {
                metricCards,
                quantilePlot: null,
                density: null,
                histogram: null,
            };
        }

        const xDomain = requiredChannels.xDomain.slice(0, minLen);
        const yProbMin = requiredChannels.yProbMin.slice(0, minLen);
        const yProbLow = requiredChannels.yProbLow.slice(0, minLen);
        const yProbExp = requiredChannels.yProbExp.slice(0, minLen);
        const yProbMid = requiredChannels.yProbMid.slice(0, minLen);
        const yProbHigh = requiredChannels.yProbHigh.slice(0, minLen);
        const yProbMax = requiredChannels.yProbMax.slice(0, minLen);
        const yTrue = requiredChannels.yTrue.slice(0, minLen);
        const yProbBinary = requiredChannels.yProbBinary.slice(0, minLen);

        const numPointsPerLocalAverage = Math.min(75, 0.1 * xDomain.length);
        const xSorted = [...xDomain].sort((a, b) => a - b);
        const spacings = xSorted.slice(1).map((value, idx) => value - xSorted[idx]);
        const meanSpacing = spacings.length > 0
            ? spacings.reduce((sum, value) => sum + value, 0) / spacings.length
            : Number.NaN;

        let autoSigma = numPointsPerLocalAverage * meanSpacing;
        if (!Number.isFinite(autoSigma) || autoSigma <= 0) {
            autoSigma = numPointsPerLocalAverage;
        }

        autoSigma = clampDcmSigma(autoSigma);
        const sigma = useAutoDcmSigma ? autoSigma : clampDcmSigma(dcmSigma);

        if (!Number.isFinite(sigma) || sigma <= 0) {
            return {
                metricCards,
                quantilePlot: null,
                density: null,
                histogram: null,
            };
        }

        const xMin = Math.min(...xDomain);
        const xMax = Math.max(...xDomain);
        let xOutMin = xMin + sigma;
        let xOutMax = xMax - sigma;
        let step = (xOutMax - xOutMin) / 1000;

        if (!Number.isFinite(step) || step <= 0 || xOutMin >= xOutMax) {
            xOutMin = xMin - 0.01;
            xOutMax = xMax + 0.02;
            step = 0.01;
        }

        const xOutPlot: number[] = [];
        let current = xOutMin;
        while (current <= xOutMax && xOutPlot.length < 1200) {
            xOutPlot.push(current);
            current += step;
        }

        if (xOutPlot.length === 0) {
            return {
                metricCards,
                quantilePlot: null,
                density: null,
                histogram: null,
            };
        }

        const weightedMean = (yValues: number[]) => {
            return xOutPlot.map((xOut) => {
                let weightedSum = 0;
                let weightTotal = 0;

                for (let i = 0; i < xDomain.length; i += 1) {
                    const weight = Math.exp(-0.5 * Math.pow((xDomain[i] - xOut) / sigma, 2));
                    weightedSum += weight * yValues[i];
                    weightTotal += weight;
                }

                return weightTotal > 0 ? weightedSum / weightTotal : 0;
            });
        };

        return {
            metricCards,
            quantilePlot: {
                xOutPlot,
                xMin,
                xMax,
                sigma,
                yOutProbMin: weightedMean(yProbMin),
                yOutProbLow: weightedMean(yProbLow),
                yOutProbExp: weightedMean(yProbExp),
                yOutProbMid: weightedMean(yProbMid),
                yOutProbHigh: weightedMean(yProbHigh),
                yOutProbMax: weightedMean(yProbMax),
                yOutTrue: weightedMean(yTrue),
                yOutProbBinary: weightedMean(yProbBinary),
            },
            density: {
                x: xDomain,
                y: yProbExp,
                xMin,
                xMax,
                normalized: buildNormalizedDensityHeatmap(xDomain, yProbExp, 50),
            },
            histogram: {
                xDomain,
                xMin,
                xMax,
                xOutMin,
                xOutMax,
            },
        };
    }, [model, modelTaskType, dcmSigma, useAutoDcmSigma]);

    const handleDcmSigmaChange: Dispatch<SetStateAction<number>> = (nextSigma) => {
        const currentSigma = dcmPlotData.quantilePlot?.sigma ?? dcmSigma;
        const resolved = typeof nextSigma === 'function'
            ? (nextSigma as (prev: number) => number)(currentSigma)
            : nextSigma;
        setUseAutoDcmSigma(false);
        setDcmSigma(clampDcmSigma(resolved));
    };

    let dropdownOptions: DropdownOption[] = [
        {
            label: 'Export Model',
            action: async (e:MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                let message = '';
                let isSuccess = true;
                const item = { name: model && model.name ? model.name : '', hash: model && model.file_hash ? model.file_hash : '' };
                try {
                    await exportData(
                        [item],
                        '.joblib',
                        'model',
                    );
                    message = `Model successfully downloaded`;
                    
                } catch (err:any) {
                    console.log(err);
                    message = 'There was an error downloading this model';
                    isSuccess = false;
                };

                setPopupMessage(message, isSuccess);
            },
        },
        {
            label: 'Export Model Stats',
            action: async (e:MouseEvent<HTMLButtonElement>) => {
                e.preventDefault();
                if (model && modelData) {
                    let message = '';
                    let isSuccess = true;
                    try {
                        await downloadModelStats(
                            '.xlsx',
                            model,
                            modelData,
                        );
                        message = `Model successfully downloaded`;
                        
                    } catch (err:any) {
                        console.log(err);
                        message = 'There was an error downloading this model';
                        isSuccess = false;
                    };

                    setPopupMessage(message, isSuccess);
                }
                
            },
        },
    ];

    if (canAccess({or: demo_access})) dropdownOptions.push(
        {
            label: 'Delete',
            btnComp: (
                <DeleteModal 
                    items={[{
                        type: 'model',
                        id: model_id ? model_id : '',
                        name: getMethodName(),
                    }]}
                />
            ),
        },
    );

    const itemMetadata = useMemo(() => {
        if (!model) return [];

        const metadata: {
            label: string,
            value: string | string[],
            adornment?: ReactNode,
        }[] = [];

        let descriptors: string[] = [];

        const { model_metadata } = model;
        const fingerprint_metadata = model_metadata?.fingerprint_metadata;

        if (fingerprint_metadata && fingerprint_metadata.job_metadata) {
            const job_metadata = fingerprint_metadata.job_metadata;
            const { fingerprint_functions } = job_metadata;

            if (fingerprint_functions && fingerprint_functions.length > 0) {
                fingerprint_functions.forEach(fingerprint => descriptors.push(fingerprint.name ?? ''))
            };
        }

        descriptors = Array.from(new Set(descriptors)).filter(desc => desc);

        if (descriptors.length > 0) {
            metadata.push({
                label: 'Fingerprints',
                value: descriptors,
                adornment: <FingerprintIcon />,
            })
        };

        return metadata;


    }, [model]);
    

    return (
        <div className="p-10 h-full flex flex-col justify-start items-stretch gap-4">
            <SinglePageHeader 
                id='single-model-header' 
                label={getMethodName()} 
                isLoading={getModelMutation.isLoading} 
                pages={[
                    {
                        label: 'Model Families',
                        action: (e: MouseEvent<HTMLButtonElement>) => {navigate('/models')}
                    },
                    {
                        label: modelFamily ? modelFamily.name! : '',
                        action: (e: MouseEvent<HTMLButtonElement>) => {navigate(`/models/${model_family_id}`)}
                    },
                    {
                        label: getMethodName(),
                    }
                ]}
                itemMetadata={itemMetadata}
            >
                <Dropdown
                    btn={{
                        label: 'Options',
                        type: 'primary',
                        styling: 'solid',
                        id: 'single-file-options'
                    }}
                    options={dropdownOptions}
                />
                {modelMenuMetadata && (
                    <UpdatedMetadataMenu 
                        menuLabel='Model Metadata'
                        menuItems={modelMenuMetadata}
                        updateFunc={updateModel}
                        location="page"
                    />
                )}
            </SinglePageHeader>

            {getModelFamilyMutation.isLoading ? (
                <div className="flex w-full justify-between items-stretch h-[700px] gap-5">
                    <div className="w-[250px] h-full"><Skeleton variant='rounded' width='100%' height='100%'/></div>
                    <div className="grow w-full h-full"><Skeleton variant='rounded' width='100%' height='100%'/></div>
                </div>
            ) : (
                <>
                {modelTaskType === 'dcm' && (
                    <div className="w-full flex flex-col gap-4">
                        {dcmMetadataRows.length > 0 && (
                            <div className="w-full dcm-metadata flex flex-col justify-start items-stretch bg-white rounded-2xl border border-outline">
                                <div className="flex gap-2 px-6 py-4">
                                    <p className="text-default font-semibold">DCM Model Details</p>
                                </div>

                                <div className="p-6 grid grid-cols-2 auto-rows-auto gap-x-6 gap-y-8">
                                    {dcmMetadataRows.map(row => (
                                        <InfoContainer key={row.label} label={row.label}>
                                            <div className="bg-[#F5F5F5] py-1 px-3 rounded-lg text-default font-medium">
                                                {row.value}
                                            </div>
                                        </InfoContainer>
                                    ))}
                                </div>
                            </div>
                        )}

                        {(dcmPlotData.metricCards.length > 0 || dcmPlotData.quantilePlot || dcmPlotData.density || dcmPlotData.histogram) && (
                            <div className="w-full dcm-diagnostics flex flex-col justify-start items-stretch bg-white rounded-2xl border border-outline">
                                <div className="flex gap-2 px-6 py-4">
                                    <p className="text-default font-semibold">DCM Diagnostics</p>
                                </div>

                                <div className="px-6 pb-6 flex flex-col gap-6">
                                    {dcmPlotData.metricCards.length > 0 && (
                                        <div className="w-full rounded-xl border border-outline p-4">
                                            <p className="text-default font-semibold mb-3">DCM Performance Metrics</p>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                {dcmPlotData.metricCards.map((metric) => (
                                                    <InfoContainer key={metric.label} label={metric.label}>
                                                        <div className="bg-[#F5F5F5] py-1 px-3 rounded-lg text-default font-medium">
                                                            {convertNumberToDecimal(metric.value)}
                                                        </div>
                                                    </InfoContainer>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {dcmPlotData.quantilePlot && (
                                        <div className="w-full rounded-xl border border-outline p-2">
                                            <div className="px-2 pt-2 pb-1 flex flex-col md:flex-row md:items-center gap-2">
                                                <p className="text-default font-semibold">DCM Quantile Plot</p>
                                                <div className="w-full md:w-auto md:ml-auto flex items-center justify-end gap-3">
                                                    <div className="w-full md:w-[390px]">
                                                        <HorizontalNumberSlider
                                                            label={'Sigma'}
                                                            threshold={useAutoDcmSigma ? dcmPlotData.quantilePlot.sigma : dcmSigma}
                                                            setThreshold={handleDcmSigmaChange}
                                                            hasBorderAndPadding={false}
                                                            range={{
                                                                min: DCM_SIGMA_MIN,
                                                                max: DCM_SIGMA_MAX,
                                                                step: 0.005,
                                                                inputStep: 0.005,
                                                            }}
                                                        />
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="px-3 py-1.5 rounded border border-outline bg-white hover:bg-[#F5F5F5] text-sm font-medium"
                                                        onClick={() => setUseAutoDcmSigma(true)}
                                                    >
                                                        Reset
                                                    </button>
                                                </div>
                                            </div>
                                            <Plot
                                                data={[
                                                    {
                                                        x: dcmPlotData.quantilePlot.xOutPlot,
                                                        y: dcmPlotData.quantilePlot.yOutProbMin,
                                                        mode: 'lines',
                                                        line: { width: 0 },
                                                        name: 'Min',
                                                        showlegend: false,
                                                    },
                                                    {
                                                        x: dcmPlotData.quantilePlot.xOutPlot,
                                                        y: dcmPlotData.quantilePlot.yOutProbLow,
                                                        mode: 'lines',
                                                        line: { width: 0 },
                                                        fill: 'tonexty',
                                                        fillcolor: 'rgba(14, 165, 233, 0.22)',
                                                        name: 'Min-Low',
                                                        showlegend: false,
                                                    },
                                                    {
                                                        x: dcmPlotData.quantilePlot.xOutPlot,
                                                        y: dcmPlotData.quantilePlot.yOutProbMid,
                                                        mode: 'lines',
                                                        line: { width: 0 },
                                                        fill: 'tonexty',
                                                        fillcolor: 'rgba(34, 197, 94, 0.2)',
                                                        name: 'Low-Mid',
                                                        showlegend: false,
                                                    },
                                                    {
                                                        x: dcmPlotData.quantilePlot.xOutPlot,
                                                        y: dcmPlotData.quantilePlot.yOutProbHigh,
                                                        mode: 'lines',
                                                        line: { width: 0 },
                                                        fill: 'tonexty',
                                                        fillcolor: 'rgba(245, 158, 11, 0.2)',
                                                        name: 'Mid-High',
                                                        showlegend: false,
                                                    },
                                                    {
                                                        x: dcmPlotData.quantilePlot.xOutPlot,
                                                        y: dcmPlotData.quantilePlot.yOutProbMax,
                                                        mode: 'lines',
                                                        line: { width: 0 },
                                                        fill: 'tonexty',
                                                        fillcolor: 'rgba(244, 63, 94, 0.2)',
                                                        name: 'High-Max',
                                                        showlegend: false,
                                                    },
                                                    {
                                                        x: dcmPlotData.quantilePlot.xOutPlot,
                                                        y: dcmPlotData.quantilePlot.yOutProbExp,
                                                        mode: 'lines',
                                                        line: { color: '#1d4ed8', width: 2 },
                                                        name: 'y_prob_exp',
                                                    },
                                                    {
                                                        x: dcmPlotData.quantilePlot.xOutPlot,
                                                        y: dcmPlotData.quantilePlot.yOutTrue,
                                                        mode: 'lines',
                                                        line: { color: '#0f766e', width: 2, dash: 'dot' },
                                                        name: 'y_true',
                                                    },
                                                    {
                                                        x: dcmPlotData.quantilePlot.xOutPlot,
                                                        y: dcmPlotData.quantilePlot.yOutProbBinary,
                                                        mode: 'lines',
                                                        line: { color: '#7c3aed', width: 2, dash: 'dash' },
                                                        name: 'y_prob_binary',
                                                    },
                                                ] as any[]}
                                                layout={{
                                                    xaxis: {
                                                        title: 'Predicted Probability of Being Out-of-Domain',
                                                        range: [dcmPlotData.quantilePlot.xMin, dcmPlotData.quantilePlot.xMax],
                                                    },
                                                    yaxis: { title: 'Probability of Activity' },
                                                    margin: { l: 60, r: 20, t: 60, b: 60 },
                                                    autosize: true,
                                                }}
                                                config={{ displaylogo: false, responsive: true }}
                                                style={{ width: '100%', height: '420px' }}
                                            />
                                        </div>
                                    )}

                                    {(dcmPlotData.density || dcmPlotData.histogram) && (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {dcmPlotData.density && (
                                                <div className="w-full rounded-xl border border-outline p-2">
                                                    <div className="px-2 pt-2 pb-1 flex justify-between items-center">
                                                        <p className="text-default font-semibold">Activity vs OOD Density</p>
                                                        <div className="flex gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => setDcmDensityMode('hist2d')}
                                                                className={`px-2 py-1 text-xs rounded border ${dcmDensityMode === 'hist2d' ? 'bg-[#E7F0FF] border-[#2563eb]' : 'bg-white border-outline'}`}
                                                            >
                                                                Hist2D
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => setDcmDensityMode('hist2d_normalized')}
                                                                className={`px-2 py-1 text-xs rounded border ${dcmDensityMode === 'hist2d_normalized' ? 'bg-[#E7F0FF] border-[#2563eb]' : 'bg-white border-outline'}`}
                                                            >
                                                                Normalized
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <Plot
                                                        data={dcmDensityMode === 'hist2d' ? [
                                                            {
                                                                type: 'histogram2d',
                                                                x: dcmPlotData.density.x,
                                                                y: dcmPlotData.density.y,
                                                                nbinsx: 50,
                                                                nbinsy: 50,
                                                                colorscale: densityColorScale,
                                                                colorbar: { title: 'Density' },
                                                            },
                                                        ] as any[] : dcmPlotData.density.normalized ? [
                                                            {
                                                                type: 'heatmap',
                                                                x: dcmPlotData.density.normalized.xCenters,
                                                                y: dcmPlotData.density.normalized.yCenters,
                                                                z: dcmPlotData.density.normalized.z,
                                                                colorscale: densityColorScale,
                                                                colorbar: { title: 'Normalized Density' },
                                                            },
                                                        ] as any[] : []}
                                                        layout={{
                                                            title: {
                                                                text: dcmDensityMode === 'hist2d' ? 'Density Heatmap' : 'Density Heatmap (Normalized)',
                                                                font: { family: 'Montserrat', size: 16 },
                                                            },
                                                            xaxis: {
                                                                title: 'Predicted Probability of Being Out-of-Domain',
                                                                range: [dcmPlotData.density.xMin, dcmPlotData.density.xMax],
                                                            },
                                                            yaxis: { title: 'Activity' },
                                                            margin: { l: 60, r: 20, t: 60, b: 60 },
                                                            autosize: true,
                                                        }}
                                                        config={{ displaylogo: false, responsive: true }}
                                                        style={{ width: '100%', height: '420px' }}
                                                    />
                                                </div>
                                            )}

                                            {dcmPlotData.histogram && (
                                                <div className="w-full rounded-xl border border-outline p-2">
                                                    <div className="px-2 pt-2 pb-1 flex justify-between items-center">
                                                        <p className="text-default font-semibold">OOD Probability Histogram with Cutoffs</p>
                                                    </div>
                                                    <Plot
                                                        data={[
                                                            {
                                                                type: 'histogram',
                                                                x: dcmPlotData.histogram.xDomain,
                                                                nbinsx: 50,
                                                                marker: { color: '#3384c6' },
                                                                opacity: 0.85,
                                                            },
                                                        ] as any[]}
                                                        layout={{
                                                            xaxis: {
                                                                title: 'Predicted Probability of Being Out-of-Domain',
                                                                range: [dcmPlotData.histogram.xMin, dcmPlotData.histogram.xMax],
                                                            },
                                                            yaxis: { title: 'Count' },
                                                            shapes: [
                                                                {
                                                                    type: 'line',
                                                                    x0: dcmPlotData.histogram.xOutMin,
                                                                    x1: dcmPlotData.histogram.xOutMin,
                                                                    y0: 0,
                                                                    y1: 1,
                                                                    yref: 'paper',
                                                                    line: { color: '#f59e0b', width: 2, dash: 'dash' },
                                                                },
                                                                {
                                                                    type: 'line',
                                                                    x0: dcmPlotData.histogram.xOutMax,
                                                                    x1: dcmPlotData.histogram.xOutMax,
                                                                    y0: 0,
                                                                    y1: 1,
                                                                    yref: 'paper',
                                                                    line: { color: '#f59e0b', width: 2, dash: 'dash' },
                                                                },
                                                            ],
                                                            annotations: [
                                                                {
                                                                    x: dcmPlotData.histogram.xOutMin,
                                                                    y: 1.04,
                                                                    yref: 'paper',
                                                                    text: 'Lower cutoff',
                                                                    showarrow: true,
                                                                    arrowhead: 2,
                                                                    ax: 0,
                                                                    ay: -22,
                                                                    font: { size: 11, color: '#9a3412' },
                                                                },
                                                                {
                                                                    x: dcmPlotData.histogram.xOutMax,
                                                                    y: 1.04,
                                                                    yref: 'paper',
                                                                    text: 'Upper cutoff',
                                                                    showarrow: true,
                                                                    arrowhead: 2,
                                                                    ax: 0,
                                                                    ay: -22,
                                                                    font: { size: 11, color: '#9a3412' },
                                                                },
                                                            ],
                                                            margin: { l: 60, r: 20, t: 60, b: 60 },
                                                            autosize: true,
                                                        }}
                                                        config={{ displaylogo: false, responsive: true }}
                                                        style={{ width: '100%', height: '420px' }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className={`model-data-grid ${modelTaskType === 'regression' ? 'regression' : 'classification'}`}>
                    <div className="w-full stats flex flex-col justify-start items-stretch bg-white rounded-2xl border border-outline">
                        <div className="flex gap-2  px-6 py-4">
                            <p className="text-default font-semibold">Method Statistics</p>
                        </div>

                        <div className="p-6 grid grid-cols-2 auto-rows-auto gap-x-6 gap-y-8">
                        {stats.length > 0 && stats.map(stat => {
                            const label = stat.associatedColumn.split('_').map(word => capitalize(word)).join(' ');
                            return (
                                <InfoContainer label={label}>
                                    <ModelStat name={stat.associatedColumn} value={stat.value as string}/>
                                </InfoContainer>
                            )
                        })}
                        </div>
                    </div>

                    {modelData && plotTypes.length > 0 && plotTypes.map(ord => {
                        const plotField = modelData.fields.find(field => field.associatedColumn === ord);

                        if (plotField) {
                            return (
                                <div className={`w-full ${ord} flex flex-col justify-start items-stretch bg-white rounded-2xl border border-outline`}>
                                    <div className="flex gap-2  px-6 py-4">
                                        <p className="text-default font-semibold">
                                            {
                                                plotField.associatedColumn === 'cross_val_predict' ? 'Cross-Validation Predictions'
                                                : plotField.associatedColumn === 'roc' ? '5-Fold Cross Validation'
                                                : plotField.associatedColumn === 'overlay' ? 'Model Evaluation Overlay'
                                                : 'Truth Table'
                                            }
                                        </p>
                                    </div>

                                    <div className="w-full px-6 py-4">
                                        {ord === 'truthTable' ? (
                                            <TruthTable 
                                                data={plotField.value as { name: string, value: number }[]}
                                            />
                                        ) : (
                                            <PlotlyGraphs 
                                                data={plotField.value as Plotly.Data[]}
                                                type={
                                                    plotField.associatedColumn === 'roc' ? 'roc' 
                                                    : plotField.associatedColumn === 'overlay' ? 'histogram'
                                                    : plotField.associatedColumn === 'cross_val_predict' ? 'scatter'
                                                    : 'scatter'
                                                }
                                                isSquare={false}
                                            />
                                        )}
                                    </div>
                                </div>
                            )
                        } else {
                            return null;
                        }
                    })}

                </div>
                </>
            )}

            <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  


        </div>
    )
}